import { type RefObject, useCallback, useEffect } from 'react';

/**
 * Hook for tracking the height of the footer and updating the CSS variable --footer-height
 * @param footerRef - ref on the footer element
 * @param dependencies - array of dependencies, when they change, the height needs to be updated (e.g. pathname)
 * @param isHidden - optional flag indicating whether the footer is hidden (if true, sets 0px)
 */
export const useFooterHeight = (
  footerRef: RefObject<HTMLElement>,
  dependencies: unknown[] = [],
  isHidden?: boolean,
) => {
  const updateFooterHeight = useCallback(() => {
    if (isHidden) {
      const els = Array.from(document.querySelectorAll<HTMLElement>('#app'));
      els.forEach((el) => el.style.setProperty('--footer-height', '0px'));
      return;
    }

    const footerElement = footerRef.current;
    if (!footerElement) {
      return;
    }

    const height = footerElement.offsetHeight;
    const els = Array.from(document.querySelectorAll<HTMLElement>('#app'));

    els.forEach((el) => el.style.setProperty('--footer-height', `${height}px`));
  }, [footerRef, isHidden]);

  useEffect(() => {
    if (isHidden) {
      updateFooterHeight();
      return;
    }

    const footerElement = footerRef.current;
    if (!footerElement) {
      const timeoutId = setTimeout(() => {
        updateFooterHeight();
      }, 0);
      return () => clearTimeout(timeoutId);
    }

    const resizeObserver = new ResizeObserver(() => {
      updateFooterHeight();
    });

    resizeObserver.observe(footerElement);

    updateFooterHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateFooterHeight, isHidden, ...dependencies]);
};
